import { action } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'

import { WalletProfile } from '../model'

import type { ProfileWindow } from '../model'
import type { Address } from '@root/entities/address/Address'

/**
 * How many tokens to ask for.
 *
 * The backend sorts positions by PnL and this is a page of them, not the
 * whole history: a wallet that has traded thousands of tokens would otherwise
 * send a payload nobody reads to draw a list nobody scrolls.
 */
const POSITIONS = 50

export const fetchWalletProfileRequestAction = action(
  async (
    ctx,
    {
      network,
      walletAddress,
      walletAddresses,
      window,
    }: {
      network: Network.Network
      walletAddress: Address
      /**
       * Several wallets, read as one portfolio. The server adds them up; a
       * holding is the last state each wallet reported, so summing answers
       * here would return one wallet's balance and call it the total.
       */
      walletAddresses?: ReadonlyArray<Address>
      window: ProfileWindow
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.services.servicesWalletProfileList, [
        {
          network: Network.toDto(network),
          walletAddress,
          ...(walletAddresses && walletAddresses.length > 0
            ? { wallets: walletAddresses.join(',') }
            : {}),
          ...window,
          limit: POSITIONS,
        },
      ]),
    )

    // Narrowed to the one error type the dashboard's components accept; what
    // went wrong is already logged where it happened.
    return result.pipe(
      Result.map(WalletProfile.fromDto),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'fetchWalletProfileRequestAction',
)
